import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  Description as TemplateIcon,
  History as HistoryIcon,
  Radio as BroadcastIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Import SMS components (we'll create these next)
import MessageComposer from './MessageComposer';
import TemplateManager from './TemplateManager';
import MessageHistory from './MessageHistory';
import BroadcastCenter from './BroadcastCenter';

const SMSDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [smsStatus, setSmsStatus] = useState(null);

  // Check SMS service status on component mount
  useEffect(() => {
    checkSMSStatus();
  }, []);

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      switch (hash) {
        case '#compose':
          setActiveTab(0);
          break;
        case '#templates':
          setActiveTab(1);
          break;
        case '#message-history':
          setActiveTab(2);
          break;
        case '#broadcast-center':
          setActiveTab(3);
          break;
        default:
          // If no hash or unknown hash, default to compose
          setActiveTab(0);
      }
    };

    // Handle initial hash on component mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const checkSMSStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/status`);
      const data = await response.json();
      setSmsStatus(data);
    } catch (error) {
      console.error('Error checking SMS status:', error);
      setError('Failed to check SMS service status');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Update URL hash to match the selected tab
    const hashMap = ['#compose', '#templates', '#message-history', '#broadcast-center'];
    const newHash = hashMap[newValue];
    if (newHash && window.location.hash !== newHash) {
      window.location.hash = newHash;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <MessageComposer />;
      case 1:
        return <TemplateManager />;
      case 2:
        return <MessageHistory />;
      case 3:
        return <BroadcastCenter />;
      default:
        return <MessageComposer />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            mb: 2
          }}
        >
          SMS Messaging Center
        </Typography>
        
        {/* SMS Status */}
        {smsStatus && (
          <Alert 
            severity={smsStatus.configured ? 'success' : 'error'}
            sx={{ mb: 2 }}
          >
            {smsStatus.message}
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(220, 38, 38, 0.15)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <SendIcon sx={{ fontSize: 40, color: '#DC2626', mb: 2 }} />
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                Send SMS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send individual messages
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="primary"
                onClick={() => {
                  setActiveTab(0);
                  window.location.hash = '#compose';
                }}
              >
                Compose
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(220, 38, 38, 0.15)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TemplateIcon sx={{ fontSize: 40, color: '#DC2626', mb: 2 }} />
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage message templates
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="primary"
                onClick={() => {
                  setActiveTab(1);
                  window.location.hash = '#templates';
                }}
              >
                Manage
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(220, 38, 38, 0.15)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <HistoryIcon sx={{ fontSize: 40, color: '#DC2626', mb: 2 }} />
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View message history
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="primary"
                onClick={() => {
                  setActiveTab(2);
                  window.location.hash = '#message-history';
                }}
              >
                View
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(220, 38, 38, 0.15)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <BroadcastIcon sx={{ fontSize: 40, color: '#DC2626', mb: 2 }} />
              <Typography variant="h4" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                Broadcast
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send bulk messages
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="primary"
                onClick={() => {
                  setActiveTab(3);
                  window.location.hash = '#broadcast-center';
                }}
              >
                Broadcast
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTab-root': {
              color: '#6b7280',
              '&.Mui-selected': {
                color: '#DC2626'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#DC2626'
            }
          }}
        >
          <Tab 
            label="Compose Message" 
            icon={<SendIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Templates" 
            icon={<TemplateIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Message History" 
            icon={<HistoryIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Broadcast Center" 
            icon={<BroadcastIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {renderTabContent()}
      </Box>
    </Container>
  );
};

export default SMSDashboard;
